import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Typography,
    Box,
    TextField,
    Switch,
    FormControlLabel,
    Button,
    Checkbox,
} from "@mui/material";
import {
    Save,
    Cancel,
    DeleteOutline,
    TaskAltRounded,
    ErrorOutlineRounded,
    CancelRounded,
    AddOutlined,
} from "@mui/icons-material";
import GGButton from "../../../CommonComponents/GGButton/GGButton";
import GGLoaderView from "../../../UtilityComponents/GGLoaderView/GGLoaderView";
import SnackBar, { SnackTypes } from "../../../UtilityComponents/SnackBar/SnackBar";
import GGDialogueBox, { dialogueType } from "../../../UtilityComponents/GGDialogueBox/GGDialogueBox";
import UploadImagesSectionDraggable from "../../../../utils/UploadImagesSectionDraggable";
import { ErrorLogger } from "../../../../utils/Logger";
import { PriceTableRow, PriceTableRowFormInterface } from "../../AddNewGigPageComponents/PriceTableRow";
import { GigAPIHandler } from "../../../../services/NetworkClient/BackendAPIs/GigAPIHandler";
import { GigPortFolioImage } from "../../../../pages/AddNewGigPage/ListingDetailsPage/ListingdetailsPage";
import { compressImage } from "../../../../utils/FileCompressionUtils";
import { ResumableUploadTask } from "../../../../services/UploadClient/ResumableUploadTask";
import { UploadPurpose } from "../../../../services/NetworkClient/BackendAPIs/UploadAPIHandler";
import { Gig } from "../../../../Models/Gig";
import './GigEditingPage.scss';

export interface GigFormDataInterface {
  title: string;
  description: string;
  gigImageUrlsToDelete?: string[];
  imageAdditionIndexList?: number[];
  portfolioUpdated?: boolean;
  isListed: boolean;
  images: GigPortFolioImage[];
  priceTable: PriceTableRowFormInterface[];
}

// Type definitions for Gig interfaces
interface GigImageInterface {
    url: string;
    assetId?: string;
    position: number;
}

interface PriceTableInterface {
    minPrice: number;
    maxPrice: number;
    duration: number;
    durationUnit: string;
    description: string;
}

export const GigEditingPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { selectedGig } = location.state || {} as { selectedGig: Gig };

    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isDialogueOpenAlert, setIsDialogueOpenAlert] = useState(false);
    const [confirmationChecked, setConfirmationChecked] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    const snackBarRef = useRef<{ showSnackbar(): any }>(null);
    const [snackBarParams, setSnackBarParams] = useState({
        message: "",
        state: SnackTypes.error,
    });

    const initGigFormData: GigFormDataInterface = {
        title: "",
        description: "",
        images: [],
        portfolioUpdated: false,
        isListed: true,
        priceTable: [
            {
                minPrice: undefined,
                maxPrice: undefined,
                duration: undefined,
                durationUnit: "days",
                description: "",
            },
        ],
    };

    const [gigFormData, setGigFormData] = useState<GigFormDataInterface>(initGigFormData);
    const [originalGigFormData, setOriginalGigFormData] = useState<GigFormDataInterface>(initGigFormData);

    useEffect(() => {
        if (selectedGig) {
            // Convert Gig to GigFormDataInterface
            const images: GigPortFolioImage[] = selectedGig.gigImgs.map((img: GigImageInterface) => ({
                remoteUrl: img.url,
                assetId: img.assetId,
            }));

            const priceTable: PriceTableRowFormInterface[] = selectedGig.priceTable.length > 0
                ? selectedGig.priceTable.map((pt: PriceTableInterface) => ({
                    minPrice: pt.minPrice,
                    maxPrice: pt.maxPrice,
                    duration: pt.duration,
                    durationUnit: pt.durationUnit,
                    description: pt.description,
                }))
                : [
                    {
                        minPrice: undefined,
                        maxPrice: undefined,
                        duration: undefined,
                        durationUnit: "days",
                        description: "",
                    },
                ];

            const formData: GigFormDataInterface = {
                title: selectedGig.title,
                description: selectedGig.description,
                images: images,
                portfolioUpdated: false,
                isListed: selectedGig.isListed,
                priceTable: priceTable,
            };

            setGigFormData(formData);
            setOriginalGigFormData(JSON.parse(JSON.stringify(formData)));
        }
    }, [selectedGig]);

    // Handle scroll to add shadow to header
    useEffect(() => {
        const handleScroll = () => {
            // The scroll happens in the dashboard-content-scrollable container
            const scrollContainer = document.querySelector('.dashboard-content-scrollable') as HTMLElement;
            if (scrollContainer) {
                const scrollPosition = scrollContainer.scrollTop;
                setIsScrolled(scrollPosition > 10);
            } else {
                // Fallback to window scroll if container not found
                const scrollPosition = window.scrollY || document.documentElement.scrollTop;
                setIsScrolled(scrollPosition > 10);
            }
        };

        // Small delay to ensure DOM is ready
        const timeoutId = setTimeout(() => {
            // Listen to the dashboard content scrollable container
            const scrollContainer = document.querySelector('.dashboard-content-scrollable') as HTMLElement;
            if (scrollContainer) {
                scrollContainer.addEventListener('scroll', handleScroll);
                // Check initial scroll position
                handleScroll();
            }
            
            // Also listen to window scroll as fallback
            window.addEventListener('scroll', handleScroll, true);
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const scrollContainer = document.querySelector('.dashboard-content-scrollable') as HTMLElement;
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, []);

    const showSnackbar = (message: string, state: SnackTypes) => {
        setSnackBarParams({ message: message, state: state });
        snackBarRef.current?.showSnackbar();
    };

    const handleToggleEdit = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (hasUnsavedChanges && editMode) {
            // Warn user about unsaved changes
            if (!window.confirm("You have unsaved changes. Are you sure you want to exit edit mode?")) {
                return;
            }
        }
        setEditMode(event.target.checked);
        if (!event.target.checked) {
            // Reset to original data when exiting edit mode
            setGigFormData(JSON.parse(JSON.stringify(originalGigFormData)));
            setHasUnsavedChanges(false);
        }
    };

    const updateGigFormData = (fieldName: string, value: any) => {
        setGigFormData((prev) => ({ ...prev, [fieldName]: value }));
        setHasUnsavedChanges(true);
    };

    const updatePriceTableRow = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
        const { name, value } = e.target;
        const updatedPriceTable = [...gigFormData.priceTable];
        updatedPriceTable[index] = {
            ...updatedPriceTable[index],
            [name]: value,
        };
        updateGigFormData("priceTable", updatedPriceTable);
    };

    const deletePriceTableRow = (index: number) => {
        if (gigFormData.priceTable.length > 1) {
            const updatedPriceTable = gigFormData.priceTable.filter((_, i) => i !== index);
            updateGigFormData("priceTable", updatedPriceTable);
        }
    };

    const validateGigFormData = (formData: GigFormDataInterface): boolean => {
        if (formData.images.length < 3) {
            showSnackbar("Gig portfolio needs a minimum of 3 images", SnackTypes.error);
            return false;
        } else if (formData.images.length > 10) {
            showSnackbar("Gig portfolio can have a maximum of 10 images", SnackTypes.error);
            return false;
        }

        if (!formData.title.trim()) {
            showSnackbar("Gig title is required", SnackTypes.error);
            return false;
        }

        if (formData.title.length > 200) {
            showSnackbar("Gig title must be 200 characters or less", SnackTypes.error);
            return false;
        }

        if (!formData.description.trim()) {
            showSnackbar("Gig description is required", SnackTypes.error);
            return false;
        }

        if (formData.description.length > 4000) {
            showSnackbar("Gig description must be 4000 characters or less", SnackTypes.error);
            return false;
        }

        // Validate price table
        for (let i = 0; i < formData.priceTable.length; i++) {
            const row = formData.priceTable[i];
            if (!row.minPrice || !row.maxPrice || !row.duration || !row.description.trim()) {
                showSnackbar(`Please fill all fields in Price Range ${i + 1}`, SnackTypes.error);
                return false;
            }
            if (Number(row.minPrice) >= Number(row.maxPrice)) {
                showSnackbar(`Minimum price must be less than maximum price in Price Range ${i + 1}`, SnackTypes.error);
                return false;
            }
        }

        return true;
    };

    const handleFileUpload = async (file: File, purpose: UploadPurpose) => {
        return new ResumableUploadTask(file, { purpose }).start();
    };

    const handleSave = async () => {
        if (!validateGigFormData(gigFormData)) {
            return;
        }

        setLoading(true);
        try {
            const trimmedData: any = {
                ...gigFormData,
                title: gigFormData.title.trim(),
                description: gigFormData.description.trim(),
                priceTable: gigFormData.priceTable.map((rowData) => ({
                    ...rowData,
                    minPrice: Number(rowData.minPrice),
                    maxPrice: Number(rowData.maxPrice),
                    duration: Number(rowData.duration),
                    description: rowData.description.trim(),
                })),
            };

            if (gigFormData.portfolioUpdated) {
                const gigImages: Array<{ assetId?: string; url: string; position: number }> = [];
                for (const imageObj of gigFormData.images) {
                    if (imageObj.file !== undefined) {
                        const compressedFile = await compressImage(imageObj.file, 0.150);
                        const asset = await handleFileUpload(compressedFile, UploadPurpose.GIG_PORTFOLIO_IMAGE);
                        gigImages.push({ assetId: asset.assetId, url: asset.url || "", position: gigImages.length });
                    }
                    if (imageObj.remoteUrl !== undefined) {
                        gigImages.push({ assetId: imageObj.assetId, url: imageObj.remoteUrl, position: gigImages.length });
                    }
                }

                if (gigFormData.images[0]?.file) {
                    const compressedFile = await compressImage(gigFormData.images[0].file, 0.030, 0.3);
                    const asset = await handleFileUpload(compressedFile, UploadPurpose.GIG_COVER_IMAGE);
                    trimmedData.coverImgAssetId = asset.assetId;
                    trimmedData.coverImg = asset.url;
                } else if (gigFormData.images[0]?.remoteUrl) {
                    trimmedData.coverImg = gigFormData.images[0].remoteUrl;
                    trimmedData.coverImgAssetId = gigFormData.images[0].assetId;
                }

                trimmedData.gigImgs = gigImages;
            }

            await GigAPIHandler.shared.editGig(selectedGig.id, trimmedData);
            showSnackbar("Gig updated successfully", SnackTypes.success);
            setOriginalGigFormData(JSON.parse(JSON.stringify(gigFormData)));
            setHasUnsavedChanges(false);
            setEditMode(false);
        } catch (err: any) {
            ErrorLogger.log(err, 'NewGigEditingPage.handleSave');
            // NetworkErrorHandler returns the error message as a string
            // If error is a string, use it directly; otherwise try error.message
            const errorMessage = typeof err === 'string' ? err : (err?.message || err?.response?.data?.message || "Something went wrong. Please try again.");
            showSnackbar(errorMessage, SnackTypes.error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (hasUnsavedChanges) {
            if (window.confirm("You have unsaved changes. Are you sure you want to cancel?")) {
                setGigFormData(JSON.parse(JSON.stringify(originalGigFormData)));
                setHasUnsavedChanges(false);
                setEditMode(false);
            }
        } else {
            setEditMode(false);
        }
    };

    const performGigDeletion = async () => {
        setIsDialogueOpenAlert(false);
        setLoading(true);
        try {
            await GigAPIHandler.shared.deleteGigById(selectedGig.id);
            showSnackbar("Gig deleted successfully", SnackTypes.success);
            setTimeout(() => {
                navigate("/freelancer-dashboard/my-gigs");
                setLoading(false)
            }, 1000);
        } catch (err) {
            ErrorLogger.log(err, 'NewGigEditingPage.performGigDeletion');
            showSnackbar("Failed to delete gig. Please try again.", SnackTypes.error);
            setLoading(false)
        } 
    };

    if (!selectedGig) {
        return (
            <Box className="new-gig-editing-page">
                <Typography variant="h6" sx={{ textAlign: "center", padding: "2rem" }}>
                    No gig selected. Please go back and select a gig to edit.
                </Typography>
            </Box>
        );
    }

    return (
        <div className="new-gig-editing-page">
            {loading && <GGLoaderView />}

            {/* Header Section */}
            <div className={`gig-edit-header ${isScrolled ? 'scrolled' : ''}`}>
                <div className="header-left">
                    <Typography variant="h6" className="gig-title-header">
                        {gigFormData.title === "" ? "Untitled Gig" : gigFormData.title}
                    </Typography>
                </div>
                <div className="header-right">
                    <div className="vertical-divider"></div>
                    <div className="header-controls">
                        <div className="edit-mode-toggle">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editMode}
                                        onChange={handleToggleEdit}
                                        color="primary"
                                    />
                                }
                                label={editMode ? "Editing" : "Viewing"}
                            />
                        </div>
                        {editMode && (
                            <div className="header-action-buttons">
                                <Button
                                    variant="outlined"
                                    startIcon={<Cancel />}
                                    onClick={handleCancel}
                                    className="cancel-btn"
                                >
                                    Cancel
                                </Button>
                            <GGButton
                                variant="contained"
                                color="success"
                                startIcon={<Save />}
                                onClick={handleSave}
                                className="save-btn"
                                disabled={!hasUnsavedChanges}
                            >
                                Save Changes
                            </GGButton>
                            </div>
                        )}
                    </div>
                </div>
                {/* Mobile layout: edit toggle on left, buttons on right */}
                <div className="mobile-header-layout">
                    <div className="mobile-edit-toggle">
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={editMode}
                                    onChange={handleToggleEdit}
                                    color="primary"
                                />
                            }
                            label={editMode ? "Editing" : "Viewing"}
                        />
                    </div>
                    {editMode && (
                        <div className="mobile-action-buttons">
                            <Button
                                variant="outlined"
                                startIcon={<Cancel />}
                                onClick={handleCancel}
                                className="cancel-btn"
                                size="small"
                            >
                                Cancel
                            </Button>
                            <GGButton
                                variant="contained"
                                color="success"
                                startIcon={<Save />}
                                onClick={handleSave}
                                className="save-btn"
                                disabled={!hasUnsavedChanges}
                                size="small"
                            >
                                Save
                            </GGButton>
                        </div>
                    )}
                </div>
            </div>

            {/* Content Section */}
            <div className="gig-edit-content">
                {/* Listing Status Card */}
                <Box className="status-card">
                    <Box className="status-content">
                        <Box className="status-icon">
                            {gigFormData.isListed ? (
                                <TaskAltRounded sx={{ fontSize: 32, color: "#4caf50" }} />
                            ) : (
                                <ErrorOutlineRounded sx={{ fontSize: 32, color: "#f44336" }} />
                            )}
                        </Box>
                        <Box className="status-text">
                            <Typography variant="h6" className="status-title">
                                {gigFormData.isListed ? "Gig is Listed" : "Gig is Unlisted"}
                            </Typography>
                            <Typography variant="body2" className="status-description">
                                {gigFormData.isListed
                                    ? "Your gig is visible to buyers and can receive orders."
                                    : "Your gig is hidden from buyers and won't receive orders."}
                            </Typography>
                        </Box>
                    </Box>
                    {editMode && (
                        <GGButton
                            variant="outlined"
                            color={gigFormData.isListed ? "warning" : "success"}
                            onClick={() => updateGigFormData("isListed", !gigFormData.isListed)}
                            className="toggle-status-btn"
                        >
                            {gigFormData.isListed ? "Unlist Gig" : "List Gig"}
                        </GGButton>
                    )}
                </Box>

                {/* Title Section */}
                <Box className="section-card">
                    <Typography variant="h6" className="section-title">
                        Gig Title *
                    </Typography>
                    <TextField
                        fullWidth
                        value={gigFormData.title}
                        onChange={(e) => updateGigFormData("title", e.target.value)}
                        placeholder="Enter a compelling title for your gig"
                        disabled={!editMode}
                        inputProps={{ maxLength: 200 }}
                        helperText={`${gigFormData.title.length}/200 characters`}
                        className="gig-title-input"
                    />
                </Box>


                {/* Portfolio Images Section */}
                <Box className="section-card">
                    <Typography variant="h6" className="section-title">
                        Portfolio Images *
                    </Typography>
                    <Typography variant="body2" className="section-subtitle">
                        Upload 3-10 images showcasing your work. The first image will be used as the cover image.
                    </Typography>
                    <Box className="images-container">
                        <UploadImagesSectionDraggable
                            editMode={editMode}
                            gigFormData={gigFormData}
                            updateGigFormData={updateGigFormData}
                        />
                    </Box>
                </Box>

                {/* Description Section */}
                <Box className="section-card">
                    <Typography variant="h6" className="section-title">
                        Description *
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={8}
                        value={gigFormData.description}
                        onChange={(e) => updateGigFormData("description", e.target.value)}
                        placeholder="Describe your service in detail..."
                        disabled={!editMode}
                        inputProps={{ maxLength: 4000 }}
                        helperText={`${gigFormData.description.length}/4000 characters`}
                        className="gig-description-input"
                    />
                </Box>

                {/* Price Table Section */}
                <Box className="section-card">
                    <Typography variant="h6" className="section-title">
                        Pricing Ranges *
                    </Typography>
                    <Typography variant="body2" className="section-subtitle">
                        Define different price ranges for your service
                    </Typography>
                    <Box className="price-table-container">
                        {gigFormData.priceTable.map((_, index) => (
                            <Box key={index} className="price-package-wrapper">
                                <Box className="price-package-header">
                                    <Typography variant="body2" className="price-package-title">
                                        Price Range {index + 1}
                                    </Typography>
                                    {gigFormData.priceTable.length > 1 && editMode && (
                                        <Box
                                            className="delete-price-package-btn"
                                            onClick={() => deletePriceTableRow(index)}
                                        >
                                            <CancelRounded fontSize="small" />
                                        </Box>
                                    )}
                                </Box>
                                <PriceTableRow
                                    index={index}
                                    gigFormData={gigFormData}
                                    updatePriceTableRow={updatePriceTableRow}
                                    deletePriceTableRow={deletePriceTableRow}
                                    editMode={editMode}
                                />
                            </Box>
                        ))}
                    </Box>
                    {editMode && (
                        <Button
                            variant="outlined"
                            startIcon={<AddOutlined />}
                            onClick={() => {
                                updateGigFormData("priceTable", [
                                    ...gigFormData.priceTable,
                                    {
                                        minPrice: undefined,
                                        maxPrice: undefined,
                                        duration: undefined,
                                        durationUnit: "days",
                                        description: "",
                                    },
                                ]);
                            }}
                            className="add-price-row-btn"
                        >
                            Add New Price Range
                        </Button>
                    )}
                </Box>

                {/* Delete Gig Section */}
                <Box className={`section-card danger-section ${!editMode ? 'disabled-section' : ''}`}>
                    <Typography variant="h6" className="section-title danger-title">
                        Delete Gig
                    </Typography>
                    <Typography variant="body2" className="section-subtitle">
                        Once you delete a gig, there is no going back. Please be certain.
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={confirmationChecked}
                                onChange={(e) => setConfirmationChecked(e.target.checked)}
                                disabled={!editMode}
                            />
                        }
                        label="I understand this action cannot be undone"
                    />
                    <GGButton
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutline />}
                        onClick={() => setIsDialogueOpenAlert(true)}
                        disabled={!editMode || !confirmationChecked}
                        className="delete-gig-btn"
                    >
                        Delete This Gig
                    </GGButton>
                </Box>
            </div>

            {/* Snackbar */}
            <SnackBar
                ref={snackBarRef}
                severity={snackBarParams.state}
                alertDuration={5000}
                message={snackBarParams.message}
            />

            {/* Delete Confirmation Dialog */}
            <GGDialogueBox
                type={dialogueType.Alert}
                isOpen={isDialogueOpenAlert}
                successHandler={performGigDeletion}
                message="Are you sure you want to delete this gig? This action cannot be undone."
                closeHandler={() => setIsDialogueOpenAlert(false)}
            />
        </div>
    );
};
